import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage, fetcher } from "@/lib/api";
import { timeAgo } from "@/lib/labels";

const Inisial = ({ nama }) => (
  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground" aria-hidden>
    {(nama || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
  </span>
);

const Baris = ({ c, anak, canModerate, onReport, onBalas, onStatus, sibuk, sorotId }) => (
  <li id={`comment-${c.id}`}
    className={`flex scroll-mt-24 gap-2.5 -mx-2 rounded-lg px-2 py-1 ${c.id === sorotId ? "bg-blue-50 dark:bg-blue-950/40" : ""}`}
    data-testid={anak ? "comment-reply" : "comment-item"}>
    <Inisial nama={c.author?.full_name} />
    <div className="min-w-0 flex-1">
      <div className={`rounded-xl px-3 py-2 ${c.status === "published" ? "bg-muted" : "border border-dashed bg-amber-50/60 dark:bg-amber-950/20"}`}>
        <p className="text-xs font-semibold">
          {c.author?.full_name || "Anggota"}
          {c.mine && <span className="ml-1.5 font-normal text-muted-foreground">· Anda</span>}
        </p>
        <p className="whitespace-pre-wrap break-words text-sm" data-testid="comment-body">{c.body}</p>
        {c.status === "hidden" && (
          <p className="mt-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300" data-testid="comment-hidden-note">Disembunyikan moderator</p>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 text-[11px] text-muted-foreground">
        <span>{timeAgo(c.created_at)}</span>
        {!anak && <button type="button" className="font-semibold hover:underline" onClick={() => onBalas(c)} data-testid="comment-reply-button">Balas</button>}
        <button type="button" className="font-semibold hover:underline" onClick={() => onReport({ type: "comment", id: c.id })} data-testid="comment-report-button">Laporkan</button>
        {canModerate && c.status === "published" && (
          <button type="button" className="font-semibold hover:underline" disabled={sibuk}
            onClick={() => onStatus(`/school/feed/comments/${c.id}/hide`)} data-testid="comment-hide-button">Sembunyikan</button>
        )}
        {canModerate && c.status === "hidden" && (
          <button type="button" className="font-semibold hover:underline" disabled={sibuk}
            onClick={() => onStatus(`/school/feed/comments/${c.id}/restore`)} data-testid="comment-restore-button">Tampilkan kembali</button>
        )}
      </div>
      {!anak && c.replies?.length > 0 && (
        <ul className="mt-2 space-y-2 border-l pl-3">
          {c.replies.map((r) => (
            <Baris key={r.id} c={r} anak canModerate={canModerate} onReport={onReport} onStatus={onStatus} sibuk={sibuk} sorotId={sorotId} />
          ))}
        </ul>
      )}
      {!anak && c.reply_count > (c.replies?.length || 0) && (
        <p className="mt-1.5 pl-1 text-[11px] text-muted-foreground">+{c.reply_count - c.replies.length} balasan lain</p>
      )}
    </div>
  </li>
);

export function CommentThread({ post, canModerate, onReport, sorotId = null }) {
  const qc = useQueryClient();
  const key = ["post-comments", post.id];
  const q = useQuery({ queryKey: key, queryFn: () => fetcher(`/school/feed/posts/${post.id}/comments`) });
  const [induk, setInduk] = useState(null);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const sudahSorot = useRef(null);

  useEffect(() => {
    if (!sorotId || !q.data || sudahSorot.current === sorotId) return;
    const el = document.getElementById(`comment-${sorotId}`);
    if (!el) return;
    sudahSorot.current = sorotId;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [sorotId, q.data]);

  const segarkan = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["feed-posts"] });
  };
  const ubahStatus = useMutation({
    mutationFn: async (url) => (await api.post(url)).data,
    onSuccess: segarkan,
    onError: (e) => toast.error(errorMessage(e)),
  });

  const kirim = async (e) => {
    e.preventDefault();
    const isi = draft.trim();
    if (!isi) return;
    setErr("");
    try {
      await api.post(`/school/feed/posts/${post.id}/comments`, { body: isi, parent_id: induk?.id || null });
      setDraft(""); setInduk(null);
      segarkan();
    } catch (ex) {
      setErr(errorMessage(ex));
    }
  };

  if (q.isLoading) return <p className="border-t px-4 py-3 text-sm text-muted-foreground">Memuat komentar…</p>;
  if (q.error) return <p className="border-t px-4 py-3 text-sm text-[#E11D2E]">{errorMessage(q.error)}</p>;

  return (
    <div className="space-y-3 border-t px-4 py-3" data-testid="comment-thread">
      {q.data.comments.length ? (
        <ul className="space-y-3">
          {q.data.comments.map((c) => (
            <Baris key={c.id} c={c} canModerate={canModerate} onReport={onReport} onBalas={setInduk}
              onStatus={ubahStatus.mutate} sibuk={ubahStatus.isPending} sorotId={sorotId} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="comment-empty">Belum ada komentar. Jadilah yang pertama menanggapi.</p>
      )}
      {err && <p className="text-sm text-[#E11D2E]" role="alert" data-testid="comment-error">{err}</p>}
      {post.comments_enabled ? (
        <form className="flex items-start gap-2" onSubmit={kirim}>
          <div className="min-w-0 flex-1 space-y-1">
            {induk && (
              <p className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="comment-replying-to">
                Membalas <b className="truncate">{induk.author?.full_name || "komentar"}</b>
                <Button type="button" variant="ghost" size="icon" className="h-5 w-5" aria-label="Batal membalas"
                  onClick={() => setInduk(null)} data-testid="comment-reply-cancel">
                  <X className="h-3 w-3" />
                </Button>
              </p>
            )}
            <Textarea rows={1} value={draft} maxLength={1000} onChange={(e) => setDraft(e.target.value)}
              placeholder={induk ? "Tulis balasan…" : "Tulis komentar…"} aria-label="Tulis komentar" data-testid="comment-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form.requestSubmit(); } }} />
          </div>
          <Button type="submit" size="icon" disabled={!draft.trim()} aria-label="Kirim komentar" data-testid="comment-submit">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="comment-disabled">Penulis mematikan komentar pada postingan ini.</p>
      )}
    </div>
  );
}
